import type { ClientTaxRateConfig, CountyShare } from '../types';

/**
 * The editing rules behind `SurtaxEditDialog`, kept pure so they are testable without a Fluent
 * dialog in jsdom (the repo's open B3 flake lives in exactly that combination).
 *
 * Mirrors `api/Services/SurtaxValidation.cs` (spec §5.3) — range, unit, shares, duplicates — so a
 * staff member hears about a mistake before the round trip. The server stays the authority: it
 * also checks that each county resolves in the DR-15DSS table, which this side deliberately does
 * NOT carry a copy of (a second 67-county table in TypeScript would drift every January).
 */

/** Rates are entered as a percent. The FL discretionary surtax ceiling staff may enter. */
export const MAX_SURTAX_PERCENT = 2.5;

/** Share-sum tolerance in percent points — the server's 0.00001 fraction, in the entered unit. */
export const SHARE_SUM_TOLERANCE_PERCENT = 0.001;

/**
 * 🔴 Below this, a non-zero rate is a DECIMAL typed into the PERCENT field. The old dialog's hint
 * taught "0.01 = 1%"; in a percent field that habit saves 0.01% — inside the range, so the server
 * accepts it — and the return computes surtax at a hundredth of the real rate, silently. No
 * Florida county's surtax sits between 0% and 0.5% (the CY2026 table's lowest non-zero rate).
 */
export const DECIMAL_SLIP_BELOW_PERCENT = 0.1;

export interface EditRow {
  /** Stable React key — rows are added and removed, so an index would re-bind inputs. */
  key: string;
  county: string;
  /** Override, as typed, in percent. Blank = use the county table. */
  ratePercent: string;
  /** Share of taxable sales, as typed, in percent. Only meaningful with two or more rows. */
  sharePercent: string;
}

export type RateError = 'notANumber' | 'outOfRange' | 'looksLikeDecimal';
export type ShareError = 'shareRequired' | 'shareNotANumber' | 'shareNotPositive';
export type CountyError = 'countyRequired' | 'duplicateCounty';

export type ParseResult<E> = { ok: true; value: number | null } | { ok: false; error: E };

/** "1,5" (a Spanish-locale keyboard) and a trailing "%" are both read as staff meant them. */
function toNumber(input: string): number {
  let s = input.trim().replace(/%$/, '').trim();
  if (!s.includes('.')) s = s.replace(',', '.');
  // Number('') is 0, which would read a stray "%" as a real rate.
  return s === '' ? Number.NaN : Number(s);
}

export function parseRatePercent(input: string): ParseResult<RateError> {
  if (input.trim() === '') return { ok: true, value: null };
  const n = toNumber(input);
  if (!Number.isFinite(n)) return { ok: false, error: 'notANumber' };
  if (n < 0 || n > MAX_SURTAX_PERCENT) return { ok: false, error: 'outOfRange' };
  if (n > 0 && n < DECIMAL_SLIP_BELOW_PERCENT) return { ok: false, error: 'looksLikeDecimal' };
  return { ok: true, value: n };
}

export function parseSharePercent(input: string): ParseResult<ShareError> {
  if (input.trim() === '') return { ok: false, error: 'shareRequired' };
  const n = toNumber(input);
  if (!Number.isFinite(n)) return { ok: false, error: 'shareNotANumber' };
  if (n <= 0) return { ok: false, error: 'shareNotPositive' };
  return { ok: true, value: n };
}

/** Percent → stored fraction, rounded so IEEE-754 residue (0.7/100 = 0.006999…) never persists. */
export function percentToFraction(percent: number): number {
  return Number((percent / 100).toFixed(6));
}

/** Stored fraction → what the input shows. Null/undefined (use the table) is a blank field. */
export function fractionToPercentInput(fraction: number | null | undefined): string {
  if (fraction === null || fraction === undefined) return '';
  return String(Number((fraction * 100).toFixed(4)));
}

/**
 * Which record a save writes — the distinction the dialog must never blur (spec §5.2).
 *
 * - `client`: one row and no split on file → the CLIENT-level config, affecting every obligation
 *   of the client that has no split of its own. Exactly what the old dialog wrote.
 * - `allocation`: a split → an effective-dated, obligation-scoped allocation. Once a split is on
 *   file, editing it down to one row stays here: switching to the client config would leave the
 *   old split in force from its own month, and the edit would appear to do nothing.
 */
export type EditPath = 'client' | 'allocation';

export function editPath(hasExistingAllocation: boolean, rowCount: number): EditPath {
  return hasExistingAllocation || rowCount > 1 ? 'allocation' : 'client';
}

export interface RowErrors {
  county?: CountyError;
  rate?: RateError;
  share?: ShareError;
}

export interface RowsValidation {
  ok: boolean;
  rowErrors: Record<string, RowErrors>;
  /** Set when the shares do not reach 100%. A hard stop — never normalized, per §4 (3). */
  sumError: { totalPercent: number } | null;
}

export function validateRows(rows: EditRow[], path: EditPath): RowsValidation {
  const rowErrors: Record<string, RowErrors> = {};
  const seen = new Set<string>();
  const multi = rows.length > 1;
  let total = 0;
  let sharesComplete = true;

  const put = (key: string, e: RowErrors) => {
    rowErrors[key] = { ...rowErrors[key], ...e };
  };

  for (const r of rows) {
    const county = r.county.trim();
    if (path === 'allocation' && county === '') put(r.key, { county: 'countyRequired' });
    if (county !== '') {
      const norm = county.toLowerCase();
      if (seen.has(norm)) put(r.key, { county: 'duplicateCounty' });
      seen.add(norm);
    }

    const rate = parseRatePercent(r.ratePercent);
    if (!rate.ok) put(r.key, { rate: rate.error });

    if (multi) {
      const share = parseSharePercent(r.sharePercent);
      if (share.ok) total += share.value ?? 0;
      else {
        sharesComplete = false;
        put(r.key, { share: share.error });
      }
    }
  }

  const sumError = multi && sharesComplete && Math.abs(total - 100) > SHARE_SUM_TOLERANCE_PERCENT
    ? { totalPercent: Number(total.toFixed(3)) }
    : null;

  return { ok: Object.keys(rowErrors).length === 0 && sumError === null, rowErrors, sumError };
}

/** Rows → the allocation the API stores. Call only after `validateRows(...).ok`. */
export function buildAllocationCounties(rows: EditRow[]): CountyShare[] {
  return rows.map((r) => {
    const rate = parseRatePercent(r.ratePercent);
    const share = rows.length === 1 ? null : parseSharePercent(r.sharePercent);
    return {
      county: r.county.trim(),
      share: rows.length === 1 ? 1 : percentToFraction(share?.ok ? share.value ?? 0 : 0),
      surtaxRateOverride: rate.ok && rate.value !== null ? percentToFraction(rate.value) : null,
    };
  });
}

/**
 * The single client-level row → the payload the old dialog sent. Fields this dialog does not show
 * (the collection-allowance flag) are carried through from what is on file, never reset.
 */
export function buildClientConfigPayload(
  clientId: string,
  existing: ClientTaxRateConfig | null | undefined,
  r: EditRow,
): ClientTaxRateConfig {
  const rate = parseRatePercent(r.ratePercent);
  return {
    clientId,
    county: r.county.trim() || undefined,
    stateRate: existing?.stateRate ?? 0.06,
    countySurtaxRateOverride: rate.ok && rate.value !== null ? percentToFraction(rate.value) : null,
    collectionAllowanceEligible: existing?.collectionAllowanceEligible ?? true,
  };
}

/** "2026-01" → the first of that month in UTC, which the server normalizes to its own key. */
export function effectiveFromIso(yearMonth: string): string {
  return `${yearMonth}-01T00:00:00Z`;
}

/**
 * Whether a Cosmos obligation's filings carry the FL county surtax — the client-side twin of
 * `StateTaxObligationSummary.HasCountySurtax`. Seeded obligations always carry `formNumber`
 * (DataverseObligationSeedMapper stamps the profile's form), so that decides first. A hand-added
 * obligation may lack one; its type then resolves in the server's own keyword order
 * (FormProfiles.Resolve): the other FL forms first, "sales" last.
 */
export function isCountySurtaxObligation(
  o: { formNumber?: string | null; obligationType?: string | null; state?: string | null },
): boolean {
  const state = (o.state ?? '').trim().toLowerCase();
  if (state !== '' && state !== 'fl' && state !== 'florida') return false;

  const form = (o.formNumber ?? '').trim().toUpperCase();
  if (form !== '') return form === 'DR-15';

  const type = (o.obligationType ?? '').toLowerCase().replace(/_/g, ' ');
  if (/solid waste|documentary stamp|doc stamp|communication/.test(type)) return false;
  return type.includes('sales');
}
